import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import useForm from 'react-hook-form';
import * as Yup from 'yup';
import { isEmpty, isUndefined } from 'lodash';
import cn from 'classnames';
import { Box, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import contractSteps from './contractSteps';
import { ONCOCARE_CONTRACT_SIGN_PATH } from '../../constants/routes';
import WizardWrapper from '../../components/WizardWrapper';
import { goToContractStep } from '../../helpers/routeHelper';
import useDeviceResize from '../../hooks/useDeviceResize';
import Loader from '../../components/Loaders/Loader';
import { getUnlockedStepsUpToPayment } from '../../helpers/contractHelpers';
import {
  addInsuredPerson,
  clearOncoCareContract,
  clearSignerBackendErrors,
  getDraftOncoCareContractAndSaveInCache,
  removeInsuredPerson,
  removeInsuredPersonBackendErrors,
  saveDraftContract,
  saveInsuredPerson,
  setHasAddedSigner,
  setHasAgreed,
  setOrUpdateSigner,
  setPaymentMethod,
} from '../../redux/modules/oncoCareContract';
import { clearCalculation, clearPrices, getPrices, setRange } from '../../redux/modules/oncoCareCalculation';
import { getCountries, getStates } from '../../redux/modules/countries';
import ContractSummary from './ContractSummary';
import InsuredPeopleDialog from '../../components/Dialogs/InsuredPeopleDialog';
import EfesContentHeader from '../../components/EfesContentHeader';
import useSchedule from '../../hooks/useSchedule';
import useOncoCareContractRange from '../../hooks/useOncoCareContractRange';
import useIsAutomaticRecurringPayment from '../../hooks/useIsAutomaticRecurringPayment';
import {
  newOnCoCareMemberSchema,
  oncoCareContractMembersSchema,
  oncoCareContractSignerSchema,
} from '../../validations/contract/oncoCare';
import { getContractSummary, getDiscount, getDiscountedPrice, getTotalprice } from '../../helpers/oncoCare/oncoCareCalcHelpers';
import { ACTION_NAME, CONTRACT_TYPES, MEMBER_TYPES } from '../../constants';
import { EFES_CONTENT_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import useRegisterValidationFields from '../../hooks/useRegisterValidationFields';
import usePrevious from '../../hooks/usePrevious';
import { useClearAllNotifications } from '../../hooks/useClearAllNotifications';

import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';
import useStepNavigationAmountStyles from '../../components/MUI/StepNavigationAmount/makeStyles';

import OncoCareCalcHeader from '../../img/oncoCareCalcHeader.svg';
import insuredPersonIcon from '../../img/svgs/insuredPerson.svg';

const OncoCareContractContainer = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const { elementIsFocused } = useSelector((state) => state.global);

  const summaryWrapperClasses = summaryWrapperUseStyles();
  const stepNavigationAmountClasses = useStepNavigationAmountStyles({ elementIsFocused });

  const goToStep = useCallback(
    (newStep) => goToContractStep(history, match, newStep),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, match.url],
  );

  const location = useLocation();
  const isDeviceLgSize = useDeviceResize('extraLgSize');
  useClearAllNotifications();

  const [urlParams] = useState(queryString.parse(location.hash));
  const [currentStep, setCurrentStep] = useState(contractSteps[0]);
  const [openInsuredPeopleModal, setOpenInsuredPeopleModal] = useState(false);
  const [openMobileSummary, setOpenMobileSummary] = useState(false);
  const [stepNavigationBox, setStepNavigationBox] = useState({});
  const [unlockedSteps, setUnlockedSteps] = useState(
    urlParams.bfp === 'true' ? getUnlockedStepsUpToPayment(contractSteps) : [contractSteps[0].id],
  );
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);

  const {
    contractData,
    loadingDraftContract,
    savingContractInCache,
    paymentMethod,
    orderId,
    draftToken,
    insuredPersonBackendErrors,
  } = useSelector((state) => state.oncoCareContract, shallowEqual);

  const signerBackendErrors = useSelector(
    (state) => state.oncoCareContract.signerBackendErrors,
    shallowEqual,
  );

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { insuredPeople, oncoCareDetails, hasAgreedAutomaticRecurring } = contractData;
  const {
    range,
    paymentFrequency,
    prices,
    riskAmount,
    isFetchingPrice,
    paymentFrequencies,
  } = oncoCareDetails;
  const oncoCareContractRange = useOncoCareContractRange();
  const isAutomaticRecurringPayment = useIsAutomaticRecurringPayment(paymentFrequency);
  const previousHasAgreed = usePrevious(hasAgreedAutomaticRecurring);

  const automaticRecurringFormProps = useForm({
    defaultValues: {
      hasAgreed: hasAgreedAutomaticRecurring,
    },
    validationSchema: {
      hasAgreed: Yup.boolean().oneOf([true]),
    },
  });

  const { setValue, register } = automaticRecurringFormProps;
  useRegisterValidationFields(['hasAgreed'], register);
  automaticRecurringFormProps.watch('hasAgreed');

  const totalPrice = useMemo(() => getTotalprice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalPriceAfterDiscount = useMemo(() => getDiscountedPrice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalDiscount = useMemo(() => getDiscount(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const calculatedSchedules = useSchedule(paymentFrequency.value, paymentFrequencies);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getStates());
  }, [dispatch]);

  useEffect(() => {
    if (
      hasAgreedAutomaticRecurring && !isUndefined(previousHasAgreed)
      && (hasAgreedAutomaticRecurring !== previousHasAgreed)
    ) {
      setValue('hasAgreed', hasAgreedAutomaticRecurring);
    }
  }, [hasAgreedAutomaticRecurring, dispatch, setValue, previousHasAgreed]);

  const handleOpenInsuredPeopleModal = useCallback(() => {
    setOpenMobileSummary(false);
    setOpenInsuredPeopleModal(true);
  }, []);

  const memberStepParams = {
    memberType: MEMBER_TYPES.INSURED_PERSON,
    members: insuredPeople,
    memberBackendErrors: insuredPersonBackendErrors,
    memberIcon: insuredPersonIcon,
    saveMember: saveInsuredPerson,
    addMember: addInsuredPerson,
    removeMember: removeInsuredPerson,
    removeMemberBackendErrors: removeInsuredPersonBackendErrors,
    setHasAddedSigner,
    isMemberFormOpen,
    setIsMemberFormOpen,
    newMemberValidationSchema: newOnCoCareMemberSchema,
    contractMembersValidationSchema: oncoCareContractMembersSchema,
  };

  const contractSummary = useMemo(() => getContractSummary(
    totalDiscount,
    totalPrice,
    t,
    paymentFrequency,
    isFetchingPrice,
    insuredPeople,
    summaryWrapperClasses,
    i18n.language,
    handleOpenInsuredPeopleModal,
  ), [
    totalDiscount,
    totalPrice,
    t,
    paymentFrequency,
    isFetchingPrice,
    handleOpenInsuredPeopleModal,
    i18n.language,
    insuredPeople,
    summaryWrapperClasses,
  ]);

  const handleOpenMobileSummary = useCallback(() => {
    setOpenMobileSummary(!openMobileSummary);
  }, [openMobileSummary]);

  const handleSignerActionsBeforeNext = useCallback(
    (data) => {
      // set residency true by default
      dispatch(setOrUpdateSigner({ ...data, resident: 1, memberId: 0 }));
      dispatch(clearSignerBackendErrors());
      if (isAuthenticated) {
        dispatch(saveDraftContract());
      }
    },
    [dispatch, isAuthenticated],
  );

  const handleOncoCareActionsBeforeNext = useCallback(() => {
    if (isAuthenticated) {
      dispatch(saveDraftContract());
    }
  }, [dispatch, isAuthenticated]);

  const beforeNextMapping = {
    signer: handleSignerActionsBeforeNext,
    insuredPeople: handleOncoCareActionsBeforeNext,
  };

  const stepParams = {
    type: CONTRACT_TYPES.ONCO_CARE,
    data: contractData,
    ...memberStepParams,
    totalPrice: (
      (isAutomaticRecurringPayment && calculatedSchedules.length)
        ? calculatedSchedules[0].amount : totalPriceAfterDiscount
    ),
    isFetchingPrice,
    signerBackendErrors,
    handleActionsBeforeNext: beforeNextMapping[currentStep.id],
    loader: <Loader />,
    showLoader: loadingDraftContract || savingContractInCache,
    showMobileSummary: isFetchingPrice || !isEmpty(prices),
    actionName: ACTION_NAME.CONTRACT,
    paymentInfoBlockBackground: 'rgba(248,175,156)',
    handleOpenMobileSummary,
    isDeviceLgSize,
    paymentParams: {
      setPaymentMethod,
      paymentMethod,
      orderId,
      draftToken,
    },
    setStepNavigationBox,
    isAutomaticRecurringPayment,
    paymentFrequencies,
    paymentFrequency,
    automaticRecurringFormProps,
    contractSignerSchema: oncoCareContractSignerSchema,
  };

  useEffect(() => {
    if (!insuredPeople.length) {
      dispatch(clearPrices(ACTION_NAME.CONTRACT));
    } else if (!isMemberFormOpen) {
      // Note: we check isMemberFormOpen to avoid sending request
      // for every insured person Input change
      dispatch(
        getPrices(
          {
            age: null,
            insuredPeople,
            riskAmount,
          },
          ACTION_NAME.CONTRACT,
        ),
      );
    }
  }, [
    insuredPeople,
    dispatch,
    isMemberFormOpen,
    riskAmount,
  ]);

  useEffect(
    () => () => {
      dispatch(clearCalculation(ACTION_NAME.CONTRACT));
      dispatch(clearOncoCareContract());
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(setRange(
      oncoCareContractRange, ACTION_NAME.CONTRACT,
    ));
  }, [dispatch, oncoCareContractRange]);

  useEffect(() => {
    if (urlParams.bfp === 'true') {
      dispatch(getDraftOncoCareContractAndSaveInCache(urlParams.orderId, urlParams.draftToken))
        .then(() => {
          dispatch(setPaymentMethod(urlParams.paymentType));
          dispatch(setHasAgreed(true));
          window.location.hash = '';
        })
        .catch(() => {
          setUnlockedSteps([contractSteps[0].id]);
        });
    }
  }, [urlParams, dispatch]);

  useEffect(() => {
    if (signerBackendErrors) {
      goToStep(contractSteps[0]);
    } else if (insuredPersonBackendErrors) {
      goToStep(contractSteps[1]);
    }
  }, [signerBackendErrors, insuredPersonBackendErrors, goToStep]);

  return (
    <>
      <InsuredPeopleDialog
        insuredPeople={insuredPeople}
        open={openInsuredPeopleModal}
        onClose={() => {
          setOpenInsuredPeopleModal(false);
          setOpenMobileSummary(true);
        }}
      />
      <EfesContentHeader
        headerText="oncoCareContract.title"
        headerImage={OncoCareCalcHeader}
        imageWidth={50}
      />
      <WizardWrapper
        steps={contractSteps}
        stepParams={stepParams}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        unlockedSteps={unlockedSteps}
        setUnlockedSteps={setUnlockedSteps}
        goToStep={goToStep}
        hasWarningModal
        locationsWhitelist={[ONCOCARE_CONTRACT_SIGN_PATH]}
        contractSummaryClassName={summaryWrapperClasses.summaryWrapperBox}
        summaryBlock={(
          <ContractSummary
            amount={
              (isAutomaticRecurringPayment && calculatedSchedules.length)
                ? calculatedSchedules[0].amount : totalPriceAfterDiscount
            }
            contractSummary={contractSummary}
            range={range}
            openMobileSummary={openMobileSummary}
            onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
            contractData={contractData}
            isFetchingPrice={isFetchingPrice}
            riskAmount={riskAmount[0]}
            paymentFrequency={paymentFrequency}
          />
        )}
      />
      <Grid
        container
        className={cn({
          [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
        })}
        spacing={isDeviceLgSize ? 4 : 0}
      >
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: !isDeviceLgSize && 20,
          }}
        >
          <Box>{!isEmpty(stepNavigationBox) && stepNavigationBox}</Box>
        </Grid>
      </Grid>
    </>
  );
};

export default OncoCareContractContainer;
